import validator from 'validator'

import {
  Iris,
  Jsonld,
  Iri,
  DataProperty,
  ObjectProperty,
  Literal,
} from '@/rec-cache/rec-cache-types'

import { GeoCoords } from './rec-cache-geo'

const anonymousLabelPrefix = 'Anonymous '

// Used as default getter for the label property.
// Returns RDFLIB node of type literal.
// TODO move into class when tested.
// It shall be possible to override with custom label function using defineProperty on RecIndividual.prototype.
// See also https://gist.github.com/JonAbrams/7edaa3545f8fd11a1919
function defaultLabel() {
  // Used for all individuals with name
  const name = this.properties?.find((p) => p.name.value == Iris.recName)
    ?.values[0]

  // Used for apartment individuals.
  const apartmentNumber = this.properties?.find(
    (p) => p.name.value == Iris.woboDesignation
  )?.values[0]

  // Used for Entry individuals
  // Expects the components to be included.
  // Implies the entry individual must be read with aggregation by the component showing the label.
  const address = this.components.find(
    (c) => c.type.value == Iris.recPostalAddress
  )
  const entry = address?.properties?.find(
    (p) => p.name.value == Iris.recAddressLine
  )?.values[0]

  // Used for postal address individuals
  const addressLine = this.properties?.find(
    (p) => p.name.value == Iris.recAddressLine
  )?.values[0]
  const postalCode = this.properties?.find(
    (p) => p.name.value == Iris.recPostalCode
  )?.values[0]
  const city = this.properties?.find((p) => p.name.value == Iris.recCity)
    ?.values[0]
  const postalAddress =
    addressLine || postalCode || city
      ? new Literal(
          `${addressLine?.value}, ${postalCode?.value} ${city?.value}`
        )
      : null

  // Used for person individuals.
  const familyName = this.properties?.find(
    (p) => p.name.value == Iris.recFamilyName
  )?.values[0]
  const givenName = this.properties?.find(
    (p) => p.name.value == Iris.recGivenName
  )?.values[0]
  const fullName =
    familyName || givenName
      ? new Literal(`${givenName?.value} ${familyName?.value}`)
      : null

  return (
    name ||
    fullName ||
    entry ||
    apartmentNumber ||
    postalAddress ||
    new Iri(anonymousLabelPrefix + this.type?.short)
  )
}

// A REC individual comprises an IRI for id, an array of IRIs for types, and an array of Property for its (data and object) properties.
// TODO consider naming, the REC individual is really an OWL individual.
// TODO also remove namespaces from cosntructor...
// TODO move components to meta, or geo coords out (no meta)...
class RecIndividual {
  constructor(namespaces = null) {
    this._id = new Iri()
    this.types = []
    this.properties = []
    this.components = []
    this.namespaces = namespaces
    // TODO meta introduced to capture calculated information on the individual.
    this.meta = { geoCoords: new GeoCoords() }
  }

  get id() {
    return this._id
  }

  set id(value) {
    this._id = value instanceof Iri ? value : new Iri(value, this.namespaces)
  }

  get type() {
    // Returns first non named individual IRI from types array. As such doesnt support multi inheritance.
    // TODO check if type will be undefined if filtered array is empty?
    const [type] = this.types.filter((t) => t.value != Iris.owlNamedIndividual)
    return type
  }

  get isNamedIndividual() {
    return this.types.includes((t) => t.value == Iris.owlNamedIndividual)
  }

  get isDefined() {
    return !!this._id.value
  }

  get hasProperties() {
    return !!this.properties.length
  }

  get hasNoProperties() {
    return !this.properties.length
  }

  // Returns array of property name Iris.
  get dataPropNames() {
    return this.properties
      .filter((p) => p instanceof DataProperty)
      .map((p) => p.name)
  }

  addTypes(...types) {
    this.types.push(...types)
    // this.types = this.types.concat(types)
    // this.types = this.types.concat(
    //   types.map((t) => (t instanceof Iri ? t : new Iri(t, this.namespaces)))
    // )
    return this
  }

  addProperties(...properties) {
    // Check if each added property exist (have same name), and if so concatenate the value(s).
    properties.forEach((property) => {
      const existingProp = this.properties.find((p) =>
        p.name.equals(property.name)
      )
      if (existingProp) {
        property.values.forEach((v) => existingProp.addValue(v))
      } else {
        this.properties.push(property)
      }
    })

    // TODO changed as concat is not supproted by vue
    //this.properties = this.properties.concat(properties)
    return this
  }

  removeProperty(property) {
    const index = this.properties.findIndex((p) => p.name.equals(property.name))
    if (index !== -1) {
      this.properties.splice(index, 1)
    }
    // this.properties = this.properties.filter(
    //   (p) => !p.name.equals(property.name)
    // )
    return this
  }

  // TODO consider if value or IRI is most logical.
  // Parameter name is an Iri.
  getProperty(name) {
    return this.properties.find((p) => p.name.equals(name.value))
  }

  // Parameter name is Iri or value string. Returns array of Value, or undefined if name is not found.
  getValuesByName(name) {
    const nameValue = name instanceof Iri ? name.value : name
    return this.properties.find((p) => p.name.value == nameValue)?.values
  }

  // Creates a label (display name) for visualization.
  // Returns Value.
  // TODO consider to return string...
  get label() {
    return defaultLabel.call(this)
  }

  clone() {
    const newIndividual = new this.constructor(this.namespaces)
    newIndividual.id = this._id.clone()
    newIndividual.types = this.types.map((t) => t.clone())
    newIndividual.properties = this.properties.map((p) => p.clone())
    newIndividual.components = this.components.map((c) => c.clone())
    newIndividual.meta.geoCoords = this.meta.geoCoords.clone()
    return newIndividual
  }

  toJSONLD() {
    const jsonld = {}
    jsonld['@id'] = this.id.value
    jsonld['@type'] = this.types.map((t) => t.value)

    // The components are added as blank nodes.
    this.components.forEach((component) => {
      const property = this.properties.find(
        (p) =>
          p instanceof ObjectProperty &&
          p.values.find((v) => v.equals(component.id))
      )
      const value = property?.values.find((v) => v.equals(component.id))
      if (property && value) {
        // Create a blank node for the component.
        const name = property.name.value
        const componentJsonld = {}
        componentJsonld['@type'] = component.types.map((t) => t.value)
        Object.assign(
          componentJsonld,
          ...component.properties.map((p) => p.toJSONLD())
        )

        // Allow for several blank nodes on same property name.
        if (jsonld[name]) jsonld[name].push(componentJsonld)
        else jsonld[name] = [componentJsonld]
      }
    })

    // TODO if a component has been mapped then the property will not be visited. Analyze...
    // As such, if the property were targeting both blank and non blank nodes only the blank nodes will prevail.
    this.properties.forEach((property) => {
      const name = property.name.value
      if (!jsonld[name]) Object.assign(jsonld, property.toJSONLD())
    })

    // TODO fixed components... make blank nodes
    // this.properties.forEach((property) => {
    //   const key = property instanceof DataProperty ? '@value' : '@id'
    //   const values = property.values.map((v) => {
    //     const value = {}
    //     value[key] = v.value
    //     return value
    //   })
    //   jsonld[property.name.value] = values
    // })
    return [jsonld]
  }

  // TODO this parser inst used, the one in owl is to cater for namespaces...
  // Invoked with expanded JSON-LD REC individual.
  // Returns RecIndividual.
  static parse(expandedJsonLd, namespaces) {
    const individual = new RecIndividual(namespaces)

    Object.entries(expandedJsonLd).forEach(([key, values]) => {
      switch (key) {
        case Jsonld.context:
          // Context disregarded.
          break
        case Jsonld.id:
          // Id is a string.
          individual.id = values
          break
        case Jsonld.type:
          // Type is an array of strings.
          individual.addTypes(...values)
          break
        default: {
          // TODO the response from the API is falsy, object properties should be { @id } not { @value }. Refactor.
          // Property values are arrays of objects { @value, [ @language ] }
          // Determine if first element is an IRI implying an object property.

          const [value] = values

          // TODO temporary fix for JSON literals.
          // If the value includes @type : $json the property is a JSON literal which for now is encoded / stringified.
          if (value?.['@type'] == '@json') {
            console.log('JSON LITERAL')
            const property = new DataProperty(
              key,
              'JSON Literal ' + JSON.stringify(values)
            )
            individual.addProperties(property)
            break
          } else {
            const property =
              value?.[Jsonld.value] && validator.isURL(value[Jsonld.value])
                ? new ObjectProperty(key, values)
                : new DataProperty(key, values)
            individual.addProperties(property)
            break
          }
        }
      }
    })

    return individual
  }
}

export { RecIndividual }
